/**
 * /invite?token=...  — invitee landing page.
 *
 * Flow:
 *   1. If unauthenticated, redirect to /auth?next=<this URL> so the user
 *      signs up or signs in with the email the invite was issued to.
 *   2. If authenticated, POST /org-invite-accept with the token and route
 *      to /dashboard on success.
 *   3. Errors (expired / wrong email / revoked / not found) render an
 *      explanatory state with a "Back to dashboard" CTA.
 *
 * The accept handler enforces email matching server-side; we don't pre-check
 * the email client-side because that would require a separate "describe
 * invite" endpoint, and the auth-required nature of this flow already
 * prevents random users from probing tokens.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { api, ApiHttpError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface AcceptResponse {
  ok: true;
  orgId: string;
  role: 'admin' | 'analyst';
  alreadyAccepted: boolean;
}

export function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const auth = useAuth();
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'pending' }
    | { kind: 'done'; alreadyAccepted: boolean }
    | { kind: 'error'; status: number; message: string }
  >({ kind: 'idle' });

  // Stable next-url so re-renders don't loop the redirect.
  const nextUrl = useMemo(() => {
    return token ? `/invite?token=${encodeURIComponent(token)}` : '/dashboard';
  }, [token]);

  // Boolean (not the user object) so the post-accept auth.refresh() —
  // which swaps the user object identity — doesn't re-trigger the effect
  // and double-POST the accept.
  const isAuthed = !!auth.user;

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', status: 400, message: 'No invite token in URL.' });
      return;
    }
    if (!auth.isLoaded) return;
    if (!isAuthed) {
      // Unauthenticated → through /auth with next pointing back here, so the
      // user resumes the accept flow right after signing in / signing up.
      navigate(`/auth?next=${encodeURIComponent(nextUrl)}`, { replace: true });
      return;
    }

    let cancelled = false;
    setState({ kind: 'pending' });
    api
      .post<AcceptResponse>('/org-invite-accept', { token })
      .then(async (res) => {
        if (cancelled) return;
        setState({ kind: 'done', alreadyAccepted: res.alreadyAccepted });
        // The accept handler just rewrote our Cognito custom:org_id /
        // custom:org_role attributes. Force a token refresh so the new org
        // claims are in the JWT before the dashboard's queries fire, then
        // sync the SPA's auth context from the refreshed session.
        try {
          await fetchAuthSession({ forceRefresh: true });
          await auth.refresh();
        } catch {
          // Non-fatal — the api client force-refreshes on the next 401.
        }
        if (cancelled) return;
        toast.success(
          res.alreadyAccepted
            ? 'You are already a member of this team.'
            : 'Invite accepted — welcome to the team.',
        );
        navigate('/dashboard', { replace: true });
      })
      .catch((err: ApiHttpError) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          status: err.status,
          message: err.message,
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth.refresh is stable enough; depending on `auth` would re-run the accept on every provider render
  }, [token, auth.isLoaded, isAuthed, navigate, nextUrl]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Joining team…</CardTitle>
          <CardDescription>
            {state.kind === 'pending' || state.kind === 'idle'
              ? 'Verifying your invitation.'
              : state.kind === 'done'
                ? state.alreadyAccepted
                  ? 'You\'re already a member of this team.'
                  : 'You\'ve joined the team.'
                : 'There was a problem with this invite.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(state.kind === 'idle' || state.kind === 'pending') && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Hang tight — finishing up.</span>
            </div>
          )}
          {state.kind === 'done' && (
            <p className="text-sm text-muted-foreground">Redirecting to your dashboard…</p>
          )}
          {state.kind === 'error' && (
            <>
              <p className="text-sm">{state.message}</p>
              <p className="text-xs text-muted-foreground">
                {state.status === 403
                  ? 'Sign out and sign back in with the email the invite was sent to. If the link expired, ask the inviter to resend.'
                  : 'If the issue persists, ask the team owner for a fresh invite.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {state.status === 403 && (
                  <Button
                    variant="default"
                    onClick={async () => {
                      await auth.signOut();
                      navigate(`/auth?next=${encodeURIComponent(nextUrl)}`, { replace: true });
                    }}
                  >
                    Sign out & switch account
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate('/dashboard', { replace: true })}>
                  Go to dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
